import React, { useEffect, useState, useCallback } from "react";
import api from "../utils/api";
import styled, { keyframes } from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// Fix default Leaflet icons
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const fadeUp = keyframes`from{opacity:0;transform:translateY(18px) scale(0.995);}to{opacity:1;transform:none;}`;

const Page = styled.div`padding:2rem;background:linear-gradient(135deg,#fff7ed,#fff9f2);min-height:calc(100vh - 72px);`;
const Card = styled.div`background:linear-gradient(180deg,rgba(255,255,255,0.98),#fff);border-radius:16px;border:1px solid rgba(249,115,22,0.06);box-shadow:0 14px 36px rgba(9,30,66,0.06);padding:1.75rem;max-width:980px;margin:0 auto;animation:${fadeUp} 420ms cubic-bezier(.2,.9,.2,1) both;`;
const HeaderRow = styled.div`display:flex;justify-content:space-between;align-items:flex-start;gap:12px;`;
const Title = styled.h2`font-size:1.5rem;margin:0;color:#111827;`;
const MetaText = styled.div`color:#6b7280;font-size:.95rem;`;
const Grid = styled.div`margin-top:1rem;display:grid;grid-template-columns:1fr 340px;gap:1rem;@media(max-width:900px){grid-template-columns:1fr;}`;
const Details = styled.div`padding-right:.5rem;color:#111827;font-size:.95rem;p{margin:.5rem 0;}strong{color:#0f172a;}`;
const Sidebar = styled.div`padding-left:.5rem;display:flex;flex-direction:column;gap:1rem;`;
const Label = styled.h3`font-size:1rem;margin:0 0 .6rem 0;color:#111827;`;
const Gallery = styled.div`display:flex;flex-direction:column;gap:8px;`;
const ThumbRow = styled.div`display:flex;gap:8px;flex-wrap:wrap;`;
const Thumb = styled.button`width:76px;height:76px;border-radius:9px;overflow:hidden;border:1px solid #eee;padding:0;background:#fff;cursor:pointer;img{width:100%;height:100%;object-fit:cover;display:block;}`;
const MapWrap = styled.div`width:100%;height:190px;border-radius:10px;overflow:hidden;border:1px solid rgba(14,20,30,0.04);box-shadow:0 8px 20px rgba(9,30,66,0.04);`;
const MapActions = styled.div`margin-top:10px;display:flex;gap:8px;flex-wrap:wrap;align-items:center;`;
const MapButton = styled.button`display:inline-flex;align-items:center;gap:8px;padding:8px 10px;border-radius:10px;border:none;cursor:pointer;font-weight:700;background:linear-gradient(90deg,#ff7a2a,#ffb06b);color:#fff;box-shadow:0 8px 20px rgba(249,115,22,0.12);font-size:.9rem;transition:.12s;&:hover{transform:translateY(-3px);}`;
const CopyButton = styled.button`padding:8px 10px;border-radius:10px;border:1px solid rgba(14,20,30,0.06);background:#fff;color:#111827;cursor:pointer;font-weight:700;font-size:.9rem;transition:.12s;&:hover{transform:translateY(-2px);box-shadow:0 8px 20px rgba(9,30,66,0.06);}`;
const Overlay = styled.div`position:fixed;inset:0;background:rgba(7,7,7,0.75);display:flex;align-items:center;justify-content:center;z-index:9999;`;
const LightboxCard = styled.div`width:92%;max-width:1000px;background:#0f172a;border-radius:12px;padding:14px;display:flex;align-items:center;gap:12px;position:relative;`;
const LargeImageWrap = styled.div`flex:1;display:flex;align-items:center;justify-content:center;min-height:320px;max-height:80vh;overflow:hidden;`;
const LargeImg = styled.img`max-width:100%;max-height:80vh;object-fit:contain;border-radius:8px;`;
const ArrowBtn = styled.button`background:rgba(255,255,255,0.06);border:none;color:#fff;width:44px;height:44px;border-radius:9px;font-size:20px;font-weight:700;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:.12s;&:hover{transform:translateY(-3px);background:rgba(255,255,255,0.12);}`;
const CloseBtn = styled.button`position:absolute;top:10px;right:12px;background:transparent;color:#fff;border:0;font-size:22px;cursor:pointer;`;
const BackButton = styled.button`margin-top:14px;padding:10px 16px;border-radius:10px;border:none;background:#f47d39ff;color:#fff;font-weight:700;cursor:pointer;font-size:.95rem;box-shadow:0 8px 20px rgba(249,115,22,0.12);`;
const PillSmall = styled.div`padding:6px 10px;border-radius:999px;background:linear-gradient(90deg,#ff7a2a,#ffb06b);color:#fff;font-weight:700;font-size:.9rem;`;

export default function SingleEntry() {
  const { id } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchEntry = async () => {
      try {
        const res = await api.get(`/api/dataanalysis/entries/${id}`, {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        if (!mounted) return;
        setEntry(res.data.entry);
        setImages((res.data.entry.uploads || []).map(u => u.url).filter(Boolean));
      } catch { navigate("/entries"); }
      finally { if(mounted) setLoading(false); }
    };
    fetchEntry();
    return () => { mounted = false; };
  }, [id, auth.token, navigate]);

  const coords = entry?.coords?.latitude && entry?.coords?.longitude ? { lat: entry.coords.latitude, lng: entry.coords.longitude } : null;
  const handleOpenInGoogleMaps = () => coords && window.open(`https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`, "_blank");
  const handleCopyCoords = async () => { if(!coords) return; try{await navigator.clipboard.writeText(`${coords.lat},${coords.lng}`); setCopied(true); setTimeout(()=>setCopied(false),1500);}catch{window.prompt("Copy coordinates", `${coords.lat},${coords.lng}`);} };

  const openLightbox = useCallback(i=>{setIndex(i);setIsOpen(true);},[]);
  const closeLightbox = useCallback(()=>setIsOpen(false),[]);
  const prev = useCallback(()=>setIndex(i=>i<=0?images.length-1:i-1),[images.length]);
  const next = useCallback(()=>setIndex(i=>i>=images.length-1?0:i+1),[images.length]);
  useEffect(()=>{if(!isOpen)return; const onKey=e=>{if(e.key==="ArrowLeft")prev();if(e.key==="ArrowRight")next();if(e.key==="Escape")closeLightbox();}; window.addEventListener("keydown",onKey); return()=>window.removeEventListener("keydown",onKey);},[isOpen,prev,next,closeLightbox]);

  if(loading) return <Page><Card><MetaText>Loading…</MetaText></Card></Page>;
  if(!entry) return <Page><Card><MetaText>Entry not found</MetaText></Card></Page>;

  return (
    <Page>
      <Card>
        <HeaderRow>
          <div><Title>Entry Details</Title><MetaText>Submitted: {new Date(entry.createdAt).toLocaleString()}</MetaText></div>
          <PillSmall>{entry.fields?.colorCategory || "—"}</PillSmall>
        </HeaderRow>

        <Grid>
          <Details>
            {["sex","age","marks","injury","notch","remarks"].map(f=><p key={f}><strong>{f.charAt(0).toUpperCase()+f.slice(1)}:</strong> {entry.fields?.[f] || "—"}</p>)}
            <p><strong>Route ID:</strong> {entry.routeId?._id || "—"}</p>
            <p><strong>Route Name:</strong> {entry.routeId?.name || "Not assigned"}</p>
            <p><strong>Spot Coordinates:</strong> {coords ? `${coords.lat}, ${coords.lng}` : "—"}</p>
           <BackButton onClick={() => navigate(`/entries/route/${entry.routeId._id || entry.routeId}`)}>
              Back to Route
            </BackButton>
          </Details>

          <Sidebar>
            <Label>Location</Label>
            {coords ? <>
              <MapWrap>
                <MapContainer center={[coords.lat,coords.lng]} zoom={15} style={{height:"100%",width:"100%"}} scrollWheelZoom={false}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
                  <Marker position={[coords.lat,coords.lng]}><Popup>{entry.fields?.sex || "Animal"} — {entry.fields?.age}</Popup></Marker>
                </MapContainer>
              </MapWrap>
              <MapActions>
                <MapButton onClick={handleOpenInGoogleMaps}>Open in Google Maps</MapButton>
                <CopyButton onClick={handleCopyCoords}>{copied?"Copied!":"Copy coords"}</CopyButton>
              </MapActions>
            </> : <div style={{color:"#9ca3af",marginTop:6}}>No location available</div>}

            <Label>Images</Label>
            {images.length===0?<div style={{color:"#9ca3af",marginTop:8}}>No images uploaded</div>:<>
              <Gallery><ThumbRow>{images.map((src,i)=><Thumb key={i} onClick={()=>openLightbox(i)}><img src={src} alt={`thumb-${i}`}/></Thumb>)}</ThumbRow></Gallery>
            </>}
          </Sidebar>
        </Grid>
      </Card>

      {isOpen && <Overlay onClick={closeLightbox}>
        <LightboxCard onClick={e=>e.stopPropagation()}>
          <ArrowBtn onClick={prev}>‹</ArrowBtn>
          <LargeImageWrap><LargeImg src={images[index]} alt={`img-${index}`}/></LargeImageWrap>
          <ArrowBtn onClick={next}>›</ArrowBtn>
          <CloseBtn onClick={closeLightbox}>×</CloseBtn>
        </LightboxCard>
      </Overlay>}
    </Page>
  );
}
